<script setup lang="ts">
import type { PresentationsWebcasts } from '../../../../typings/PresentationsWebcasts'
import getPresentationsWebcastsByYearQuery from '@/graphql/getPresentationsWebcastsByYear.gql'
import getUpcomingWebcastQuery from '@/graphql/getUpcomingWebcast.gql'
import Pagination from '~/components/utilities/Pagination.vue'
import { ensureProtocol } from '~/helpers/urlHelpers'

const localePath = useLocalePath()
const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()

defineI18nRoute({
  paths: {
    'en-CA': '/investor-relations/financial-documents/presentations-webcasts/:year',
    'fr-CA': '/relations-avec-investisseurs/documents-financiers/presentations-webcasts/:year'
  }
})

useHead({
  title: t('navigation.presentationsWebcasts.metatitle'),
  meta: [{ name: 'description', content: t('navigation.presentationsWebcasts.metadescription') }]
})

const { execute: getPresentationsWebcastsByYear } = useApolloFetch(getPresentationsWebcastsByYearQuery, 'presentationAndWebcasts')
const { execute: getUpcomingWebcast } = useApolloFetch(getUpcomingWebcastQuery, 'presentationAndWebcasts')
const year = parseInt(route.params.year as string, 10)
const { startISOString, endISOString } = getYearStartAndEndDates(year)

const pageSize = ref<number>(parseQueryParamToNumber(route.query.pageSize, 10))
const page = ref<number>(1)
const sortBy = ref<string>(getSortByParamOrDefault('PublishedAtDate:desc'))
const presentationsWebcastsResponse = ref<{ data: PresentationsWebcasts[], meta: any }>(await getPresentationsWebcasts())
const upcomingWebcast = ref<PresentationsWebcasts>((await getUpcomingWebcast({
  locale: locale.value,
  now: new Date().toISOString(),
  startDate: startISOString,
  endDate: endISOString
}))[0])

const presentationsWebcasts = computed(() => presentationsWebcastsResponse.value.data)
const totalPresentationsWebcasts = computed(() => presentationsWebcastsResponse.value.meta.pagination.total)
const resultsIndexes = computed(() => {
  return `${(page.value - 1) * pageSize.value + 1} - ${Math.min(page.value * pageSize.value, totalPresentationsWebcasts.value)}`
})

function getFormattedPublishDateTime(dateString: string) {
  const date = new Date(dateString)
  const optionsDate: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' }
  const optionsTime: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: 'numeric' }
  const dateFormattedString = date.toLocaleDateString(locale.value, optionsDate)
  const timeString = date.toLocaleTimeString(locale.value, optionsTime)

  return `${dateFormattedString} - ${timeString} ET`
}

async function getPresentationsWebcasts() {
  return await getPresentationsWebcastsByYear({
    locale: locale.value,
    pageSize: pageSize.value,
    page: page.value,
    sortBy: sortBy.value,
    startDate: startISOString,
    endDate: endISOString
  })
}

function getSortByParamOrDefault(defaultValue: string): string {
  const param = route.query.sortBy
  if (Array.isArray(param)) {
    return param[0] ?? defaultValue
  }

  return param ?? defaultValue
}

function getSizeInMB(size: number) {
  return (size / 1024).toFixed(1)
}

function getSizeInKB(size: number) {
  return size.toFixed(1)
}

function getYearStartAndEndDates(year: number) {
  const startDate = new Date(year, 0, 1, 0, 0, 0, 0)
  const endDate = new Date(year, 11, 31, 23, 59, 59, 999)

  const startISOString = startDate.toISOString()
  const endISOString = endDate.toISOString()

  return {
    startISOString,
    endISOString
  }
}

function parseQueryParamToNumber(param: string | (string | null)[] | null | undefined, defaultValue: number): number {
  if (Array.isArray(param)) {
    param = param[0]
  }
  const parsed = parseInt(param || '', 10)
  return isNaN(parsed) ? defaultValue : parsed
};

async function refreshPresentationsWebcasts() {
  presentationsWebcastsResponse.value = await getPresentationsWebcasts()
}

watch([pageSize, sortBy, page], async ([_newPageSize, _newSortBy, newPage], [_oldPageSize, _oldSortBy, oldPage]) => {
  if (newPage === oldPage) {
    page.value = 1
  }

  router.push({
    path: route.path,
    query: {
      ...route.query,
      pageSize: pageSize.value.toString(),
      sortBy: sortBy.value,
      page: page.value.toString()
    }
  })

  await refreshPresentationsWebcasts()
})
</script>

<template>
  <div class="wrapContent">
    <div class="entetePage">
      <div class="wrapper">
        <div class="filAriane">
          <span class="">
            <NuxtLink :to="localePath('index')">{{ $t('navigation.home.name') }}</NuxtLink>
            <span class=""> &gt; </span>
            <span class="">{{ $t('navigation.newsEvents.name') }}</span>
            <span class=""> &gt; </span>
            <span class="">{{ $t('navigation.presentationsWebcasts.name') }}</span>
          </span>
        </div>
        <div class="title">
          <h1><span class="">{{ $t('navigation.presentationsWebcasts.name') }}</span></h1>
        </div>
      </div>
    </div>
    <div class="contentPage wrapAll presentations">
      <div class="upcoming wrapper">
        <div>
          <h2>{{ $t("presentationsWebcasts.upcomingEvents") }}</h2>
        </div>
        <div class="defaultFacet">
          <div class="FacetedResultTemplate DefaultResultContainer">
            <div v-if="upcomingWebcast"
                 class="ResultContent">
              <span class="date inline">{{ getFormattedPublishDateTime(upcomingWebcast.ScheduledPublishDate) }}</span>
              <span class="ResultContentProductName">{{ upcomingWebcast.Name }}</span>
            </div>
            <div v-else
                 class="ResultContent">
              <span class="ResultContentProductName">{{ $t("presentationsWebcasts.noUpcomingEvents") }}</span>
            </div>
          </div>
        </div>
        <div class="archived">
          <h2>Archived Events</h2>
        </div>
      </div>
      <div class="wrapper clearfix">
        <MenuPresentationsWebcasts />
        <div class="contenu right FacetedExplorer  presentations">
          <div class="GpcFacetedResults defaultFacet">
            <div class="enteteGPC">
              <div class="filterBlock">
                <label class="sortBy left">{{ $t("global.facets.sortBy") }}</label>
                <select v-model="sortBy">
                  <option value="PublishedAtDate:desc">
                    {{ $t("global.facets.dateDesc") }}
                  </option>
                  <option value="PublishedAtDate:asc">
                    {{ $t("global.facets.dateAsc") }}
                  </option>
                  <option value="Name:desc">
                    {{ $t("global.facets.nameDesc") }}
                  </option>
                  <option value="Name:asc">
                    {{ $t("global.facets.nameAsc") }}
                  </option>
                </select>
              </div>
              <div class="filterBlock">
                <label class="pageSize left">{{ $t("global.facets.itemsPerPage") }}</label>
                <select v-model="pageSize">
                  <option :value="10">
                    10
                  </option>
                  <option :value="25">
                    25
                  </option>
                  <option :value="50">
                    50
                  </option>
                  <option :value="100">
                    100
                  </option>
                </select>
              </div>
            </div>
            <div class="GpcResultItemWrapper">
              <div v-for="presentationWebcast in presentationsWebcasts"
                   :key="presentationWebcast.id"
                   class="FacetedResultTemplate DefaultResultContainer">
                <div class="ResultContent">
                  <div class="date">
                    {{ getFormattedPublishDateTime(presentationWebcast.PublishedAtDate) }}
                  </div>
                  <div class="ResultContentProductName">
                    {{ presentationWebcast.Name }}
                  </div>
                  <a v-if="presentationWebcast.WebcastUrl"
                     class="telechargement webcast"
                     target="_blank"
                     :href="presentationWebcast.WebcastUrl">
                    <div class="Nb546fd95a7384893b53b8fbbc8fddd69 forBroker ProductCustomResource zoneForBroker">
                      <span class="ProductCustomResource spanForBroker">{{ $t("presentationsWebcasts.listenWebcast") }}</span>
                    </div>
                  </a>
                  <a v-if="presentationWebcast.Presentation"
                     class="telechargement presentation"
                     target="_blank"
                     :href="ensureProtocol(presentationWebcast.Presentation.url)">
                    <div class="N3c7e2225bbff4c0ab650f4c36d3ac5d7 forBroker ProductCustomResource zoneForBroker">
                      <span class="ProductCustomResource spanForBroker">{{ $t("presentationsWebcasts.viewPresentation") }}</span>&nbsp;-&nbsp;<span class="ProductBrokerType_Document">{{ getSizeInMB(presentationWebcast.Presentation.size) }} MB</span>
                    </div>
                  </a>
                  <a v-if="presentationWebcast.ManagementInformationCircular"
                     class="telechargement management"
                     target="_blank"
                     :href="ensureProtocol(presentationWebcast.Presentation.url)">
                    <div class="N33f5f8517c7d48a5ae1d17f56e337d60 forBroker ProductCustomResource zoneForBroker">
                      <span class="ProductCustomResource spanForBroker">{{ $t("presentationsWebcasts.managementInformationCircular") }}</span>&nbsp;-&nbsp;<span class="ProductBrokerType_Document">{{ getSizeInKB(presentationWebcast.ManagementInformationCircular.size) }} kB</span>
                    </div>
                  </a>
                  <a v-if="presentationWebcast.VotingInstructionForm"
                     class="telechargement voting"
                     target="_blank"
                     :href="ensureProtocol(presentationWebcast.VotingInstructionForm.url)">
                    <div class="N8e122e5ebf50433a942878e5050e3a50 forBroker ProductCustomResource zoneForBroker">
                      <span class="ProductCustomResource spanForBroker">{{ $t("presentationsWebcasts.votingInstructionForm") }}</span>&nbsp;-&nbsp;<span class="ProductBrokerType_Document">{{ getSizeInMB(presentationWebcast.VotingInstructionForm.size) }} MB</span>
                    </div>
                  </a>
                  <a v-if="presentationWebcast.FormProxy"
                     class="telechargement form"
                     target="_blank"
                     :href="ensureProtocol(presentationWebcast.FormProxy.url)">
                    <div class="Nedda4284b5e74e0a9b6f683ad39dbb84 forBroker ProductCustomResource zoneForBroker">
                      <span class="ProductCustomResource spanForBroker">{{ $t("presentationsWebcasts.proxyForm") }}</span>&nbsp;-&nbsp;<span class="ProductBrokerType_Document">{{ getSizeInKB(presentationWebcast.FormProxy.size) }} kB</span>
                    </div>
                  </a>
                  <a v-if="presentationWebcast.InvestorPresentation"
                     class="telechargement investor"
                     target="_blank"
                     :href="ensureProtocol(presentationWebcast.InvestorPresentation.url)">
                    <div class="N3c7e2225bbff4c0ab650f4c36d3ac5d7 forBroker ProductCustomResource zoneForBroker">
                      <span class="ProductCustomResource spanForBroker">{{ $t("presentationsWebcasts.investorPresentation") }}</span>&nbsp;-&nbsp;<span class="ProductBrokerType_Document">{{ getSizeInMB(presentationWebcast.InvestorPresentation.size) }} MB</span>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div class="resultsPagination">
            <div class="results">
              <span>{{ $t("global.facets.results") }} <strong>{{ resultsIndexes }}</strong> {{ $t("global.facets.of") }} {{ totalPresentationsWebcasts }}</span>
            </div>
            <div class="pagination">
              <Pagination v-model="page"
                          :total-items="totalPresentationsWebcasts"
                          :page-size="pageSize" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss"></style>
